import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, FileJson2, X } from 'lucide-react';
import { useConfigs } from '@/hooks';
import { RenderIf } from '@/components/RenderIf';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ConfigTypeOption, ConfigTypeFilterProps } from './types';
import { FilterButton } from './styled';

const ConfigTypeFilter = ({ agentId }: ConfigTypeFilterProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: configs } = useConfigs({ agentId });

    const selectedConfigType = searchParams.get('type');

    const typeOptions = useMemo(() => {
        const freeformCount = configs.filter(config => config.type === 'freeform').length;
        const schemaCount = configs.filter(config => config.type === 'schema').length;

        const nextOptions: ConfigTypeOption[] = [
            {
                value: 'freeform',
                label: 'Freeform',
                count: freeformCount,
            },
            {
                value: 'schema',
                label: 'Schema',
                count: schemaCount,
            },
        ];

        return nextOptions;
    }, [configs]);

    const selectedTypeOption = useMemo(() => {
        return typeOptions.find(option => option.value === selectedConfigType) || null;
    }, [selectedConfigType, typeOptions]);

    const handleSelectType = ({ configType }: { configType: string }) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('type', configType);
        setSearchParams(nextParams);
    };

    const handleClearFilter = () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('type');
        setSearchParams(nextParams);
    };

    return (
        <DropdownMenu>
            <FilterButton>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                        <FileJson2 size={14} />
                        <RenderIf isTrue={!selectedTypeOption}>
                            <span>Filter by Type</span>
                        </RenderIf>
                        <RenderIf isTrue={!!selectedTypeOption}>
                            <span>{selectedTypeOption?.label}</span>
                            <X
                                size={14}
                                className="cursor-pointer"
                                onClick={event => {
                                    event.stopPropagation();
                                    handleClearFilter();
                                }}
                            />
                        </RenderIf>
                    </Button>
                </DropdownMenuTrigger>
            </FilterButton>
            <DropdownMenuContent align="end" className="w-56">
                {typeOptions.map(option => {
                    const isSelected = selectedConfigType === option.value;
                    return (
                        <DropdownMenuItem
                            key={option.value}
                            onClick={() =>
                                handleSelectType({
                                    configType: option.value,
                                })
                            }
                            className="gap-2"
                        >
                            <span className="flex-1">
                                {option.label} ({option.count})
                            </span>
                            <RenderIf isTrue={isSelected}>
                                <Check size={14} />
                            </RenderIf>
                        </DropdownMenuItem>
                    );
                })}

                <RenderIf isTrue={!!selectedConfigType}>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleClearFilter} className="gap-2">
                        <X size={14} />
                        <span>Clear filter</span>
                    </DropdownMenuItem>
                </RenderIf>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ConfigTypeFilter;
